/* Talking to the API and to GoTrue.
 *
 * REFUSALS ARE NOT ERRORS
 * The services return a refusal code with parameters and a 200, deliberately:
 * "services return refusal codes, never sentences", so the client renders the
 * language. A thrown exception for E_TOO_SOON would make every caller handle a
 * normal, expected outcome in a catch block, and the counter's whole job is
 * showing those outcomes well.
 *
 * So `call` throws only for things that are actually wrong: no network, no
 * session, a server fault. Everything the product has an opinion about comes
 * back as data.
 */
export type Refusal = { refusal: string; params: Record<string, unknown> };

export type ApiResult<T> = ({ status: number } & T) | ({ status: number } & Refusal);

export function isRefusal<T>(r: ApiResult<T>): r is { status: number } & Refusal {
  return typeof (r as Refusal).refusal === "string";
}

/** The service said no, with a code, at the HTTP layer rather than in a body.
 *
 * WHY THIS IS NOT A `Refusal`. A refusal is a normal outcome of a normal
 * request and comes back with a 200, so the screen can render the words. A 403
 * is the other thing: the caller may not have this at all. FastAPI's own
 * convention carries the reason in `detail`, and before this existed a 403
 * became a bare `Error("request failed 403")`, which every caller rendered as
 * "something went wrong".
 *
 * That cost the admin portal a real screen: an account that is not on Damga's
 * staff list saw a fault banner instead of a sentence saying the list is a
 * deployment setting. Found by rendering it, 2026-09-17. The code travels so
 * the screen can say which no it was. */
export class Forbidden extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(code: string | null, status = 403) {
    super(code ?? `forbidden ${status}`);
    this.name = "Forbidden";
    this.status = status;
    this.code = code;
  }
}

export class Offline extends Error {
  constructor() {
    super("offline");
    this.name = "Offline";
  }
}

/** The service refused because too many codes have been sent, not because
 * anything is broken.
 *
 * A DISTINCT TYPE BECAUSE THE SCREEN MUST SAY A DIFFERENT THING. A 429 behind
 * a generic error is a customer standing at a counter concluding the app does
 * not work, which is the same failure as the counter's grey "Saved" panel: a
 * true state rendered as the wrong one.
 *
 * It exists because SMS to Qatar costs 0.2634 USD a message, so the send rate
 * is deliberately capped low. The cap refusing is the cap working. */
export class RateLimited extends Error {
  constructor() {
    super("rate limited");
    this.name = "RateLimited";
  }
}

export class SessionExpired extends Error {
  constructor() {
    super("session expired");
    this.name = "SessionExpired";
  }
}

export type Session = {
  access_token: string;
  refresh_token: string;
  /** Epoch seconds. */
  expires_at: number;
};

export type ApiConfig = {
  apiUrl: string;
  authUrl: string;
  /** Where the session is refreshed. GoTrue's token endpoint unless said
   *  otherwise; a merchant session is the API's own (merchant.ts). */
  refreshUrl?: string;
  getSession: () => Session | null;
  setSession: (s: Session | null) => void;
};

export function createClient(config: ApiConfig) {
  async function refresh(): Promise<Session> {
    const current = config.getSession();
    if (!current) throw new SessionExpired();
    const res = await fetch(config.refreshUrl ?? `${config.authUrl}/token?grant_type=refresh_token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: current.refresh_token }),
    });
    if (!res.ok) {
      config.setSession(null);
      throw new SessionExpired();
    }
    const body = await res.json();
    const next: Session = {
      access_token: body.access_token,
      refresh_token: body.refresh_token,
      expires_at: Math.floor(Date.now() / 1000) + (body.expires_in ?? 3600),
    };
    config.setSession(next);
    return next;
  }

  async function token(): Promise<string> {
    const s = config.getSession();
    if (!s) throw new SessionExpired();
    // Refreshed a minute early rather than on expiry, so a request never
    // races the clock and fails for a reason the person cannot act on.
    if (s.expires_at - 60 <= Math.floor(Date.now() / 1000)) return (await refresh()).access_token;
    return s.access_token;
  }

  async function call<T>(
    path: string,
    init: { method?: string; body?: unknown } = {},
  ): Promise<ApiResult<T>> {
    const access = await token();
    let res: Response;
    try {
      res = await fetch(`${config.apiUrl}${path}`, {
        method: init.method ?? "GET",
        headers: {
          Authorization: `Bearer ${access}`,
          ...(init.body ? { "Content-Type": "application/json" } : {}),
        },
        body: init.body ? JSON.stringify(init.body) : undefined,
      });
    } catch {
      // fetch rejects for a dead network. navigator.onLine is not consulted:
      // it reports the radio, not reachability, and lies on captive portals
      // and on a wifi with no upstream, which is exactly the counter's case.
      throw new Offline();
    }

    if (res.status === 401) {
      await refresh();
      return call<T>(path, init);
    }
    if (res.status >= 500) throw new Error(`server error ${res.status}`);

    const body = await res.json();
    // A 4xx that is not a refusal is an ApiError, not data. Returning it as
    // data is how a counter ended up rendering a business object that was
    // actually a 404 body, with an undefined id, and only failed later at the
    // point of stamping.
    if (res.status >= 400 && typeof (body as Refusal).refusal !== "string") {
      // A 403 keeps its code, so the screen can say which no it was.
      if (res.status === 403) {
        const detail = (body as { detail?: unknown }).detail;
        throw new Forbidden(typeof detail === "string" ? detail : null);
      }
      throw new Error((body as { error?: string }).error ?? `request failed ${res.status}`);
    }
    return body as ApiResult<T>;
  }

  return { call, refresh };
}

/* GoTrue, for the sign-in screens. Called directly rather than proxied: it
 * owns the session and the API only ever validates what it issued. */
/** Which channel carries the one-time code.
 *
 * THE SEAM IS GOTRUE'S, AND IT ALREADY EXISTS. `/otp` takes a `channel`, and
 * the deployed v2.158.1 answers an unknown one with "Invalid channel,
 * supported values are 'sms' or 'whatsapp'". So switching is a configuration
 * value here plus a provider setting there, not a rewrite — which is the whole
 * reason to pass it explicitly now rather than let the server's default stand
 * in for a decision nobody made.
 *
 * Why it will matter: SMS to Qatar is 0.2634 USD a message, roughly a riyal.
 * At a merchant subscription near QAR 150 a month, about 158 messages consume
 * the entire subscription. The pilot can afford SMS; the business cannot. */
export type OtpChannel = "sms" | "whatsapp";

export function createAuth(authUrl: string, channel: OtpChannel = "sms") {
  async function authed(
    accessToken: string,
    path: string,
    body: unknown,
  ): Promise<Record<string, unknown>> {
    let res: Response;
    try {
      res = await fetch(`${authUrl}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });
    } catch {
      throw new Offline();
    }
    const parsed = await res.json().catch(() => ({}));
    if (!res.ok) {
      // The message matters here: an auth service too old to know the factor
      // type answers "factor_type needs to be totp or phone", and that is a
      // deployment fact rather than something the customer did.
      throw new Error(
        (parsed as { msg?: string; error_description?: string }).msg
          ?? (parsed as { error_description?: string }).error_description
          ?? `auth ${res.status}`,
      );
    }
    return parsed as Record<string, unknown>;
  }

  return {
    async requestCode(phoneE164: string): Promise<void> {
      // A rejected fetch and a declining server are different problems and
      // must not read the same. This one failed for a whole afternoon behind
      // "Nothing was recorded. Try once more.", which described neither: the
      // request never left the device, because a preflight was rejected.
      let res: Response;
      try {
        res = await fetch(`${authUrl}/otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // The channel is sent on every request rather than left to the
          // server's default. A default is a decision nobody made, and this
          // one costs a riyal a time when it goes the wrong way.
          body: JSON.stringify({ phone: phoneE164.replace(/^\+/, ""), channel }),
        });
      } catch {
        throw new Offline();
      }
      // 429 IS NOT A FAILURE, IT IS THE CAP DOING ITS JOB, and the customer
      // has to be told which of the two they are looking at.
      if (res.status === 429) throw new RateLimited();
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).msg ?? "could not send");
    },

    /* MFA factors live on the AUTH service, not the API.
     *
     * Passkey enrolment is a GoTrue endpoint, so it belongs beside the other
     * things that talk to GoTrue rather than being routed through the
     * application client — which would have needed a per-call switch for which
     * host to use, and a switch like that is how a request eventually goes to
     * the wrong one.
     *
     * The access token is passed in rather than read from a store, because the
     * caller already holds a live session and threading it makes it obvious at
     * the call site that these are authenticated calls. */
    async enrolFactor(accessToken: string, friendlyName: string): Promise<Record<string, unknown>> {
      return authed(accessToken, "/factors", {
        factor_type: "webauthn",
        friendly_name: friendlyName,
      });
    },

    async verifyFactor(
      accessToken: string,
      factorId: string,
      credential: Record<string, unknown>,
    ): Promise<Record<string, unknown>> {
      return authed(accessToken, `/factors/${factorId}/verify`, credential);
    },

    async challengeFactor(accessToken: string, factorId: string): Promise<Record<string, unknown>> {
      return authed(accessToken, `/factors/${factorId}/challenge`, {});
    },

    /* THE THIRD CHANNEL, and the only one that needs nobody's approval.
     *
     * Email was ruled recovery rather than a way in, and that ruling assumed a
     * phone path existed. It does not: no Qatari numbers are sold, the
     * alphanumeric sender needs a compliance profile that needs MOCI, and
     * WhatsApp needs a template on a suspended Meta account. A worse
     * experience that works beats a better one that is blocked.
     *
     * No `channel` on this request. Channel selects between SMS and WhatsApp,
     * both of which carry a phone number; sending it here would be sending a
     * parameter about a different transport entirely. */
    async requestEmailCode(email: string): Promise<void> {
      let res: Response;
      try {
        res = await fetch(`${authUrl}/otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // create_user, because a first sign-in IS the sign-up here, exactly
          // as it is on the phone path. Without it a new customer gets a
          // refusal that reads as "we do not know you" on the screen that is
          // supposed to be how we come to know them.
          body: JSON.stringify({ email, create_user: true }),
        });
      } catch {
        throw new Offline();
      }
      if (res.status === 429) throw new RateLimited();
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).msg ?? "could not send");
    },

    async verifyEmailCode(email: string, code: string): Promise<Session> {
      let res: Response;
      try {
        res = await fetch(`${authUrl}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // "email", not "sms". The type names the identifier, and sending the
          // wrong one is accepted by GoTrue and then finds no code.
          body: JSON.stringify({ type: "email", email, token: code }),
        });
      } catch {
        throw new Offline();
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.access_token) throw new Error(body.msg ?? "bad code");
      // NORMALISED, not cast. GoTrue returns expires_in on every path and
      // expires_at on only some, and casting the body straight to a Session
      // left expires_at undefined. The refresh check reads
      // `expires_at - 60 <= now`, which is NaN for undefined, which is false --
      // so the session was never refreshed and simply stopped working an hour
      // after a sign-in that had looked completely successful.
      return {
        access_token: body.access_token,
        refresh_token: body.refresh_token,
        expires_at: body.expires_at ?? Math.floor(Date.now() / 1000) + (body.expires_in ?? 3600),
      };
    },

    /* PASSKEYS AS A WAY IN, WHICH IS NOT WHAT /factors DOES.
     *
     * The four calls below are GoTrue's passkey endpoints, and they are a
     * different feature from the MFA WebAuthn factor endpoints above. The
     * factor endpoints bind a credential to a session that already exists, so
     * they can add security and cannot save a message. These sign somebody in.
     *
     * The distinction cost us a premise: the auth architecture was ruled on
     * "a passkey means one SMS per customer for life", and what was built was
     * the second-factor half, so a returning customer still needed a code.
     * /settings reported passkeys_enabled:false throughout.
     *
     * `authenticationOptions` takes NO identifier and NO token. That is the
     * point of a discoverable credential: the authenticator resolves which
     * account it is from what it stored, so there is nothing to type. */
    async passkeyRegistrationOptions(accessToken: string): Promise<Record<string, unknown>> {
      return authed(accessToken, "/passkeys/registration/options", {});
    },

    async passkeyRegistrationVerify(
      accessToken: string,
      challengeId: string,
      credential: Record<string, unknown>,
    ): Promise<Record<string, unknown>> {
      return authed(accessToken, "/passkeys/registration/verify", {
        challenge_id: challengeId,
        credential,
      });
    },

    async passkeyAuthenticationOptions(): Promise<Record<string, unknown>> {
      let res: Response;
      try {
        res = await fetch(`${authUrl}/passkeys/authentication/options`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        });
      } catch {
        throw new Offline();
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.msg ?? `auth ${res.status}`);
      return body as Record<string, unknown>;
    },

    async passkeyAuthenticationVerify(
      challengeId: string,
      credential: Record<string, unknown>,
    ): Promise<Session> {
      let res: Response;
      try {
        res = await fetch(`${authUrl}/passkeys/authentication/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ challenge_id: challengeId, credential }),
        });
      } catch {
        throw new Offline();
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.access_token) throw new Error(body.msg ?? `auth ${res.status}`);
      return {
        access_token: body.access_token,
        refresh_token: body.refresh_token,
        // Normalised, for the same reason verifyEmailCode is: an undefined
        // expires_at makes the refresh check NaN and the session dies quietly
        // an hour later.
        expires_at: body.expires_at ?? Math.floor(Date.now() / 1000) + (body.expires_in ?? 3600),
      };
    },

    async verifyCode(phoneE164: string, code: string): Promise<Session> {
      let res: Response;
      try {
        res = await fetch(`${authUrl}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "sms",
            phone: phoneE164.replace(/^\+/, ""),
            token: code,
          }),
        });
      } catch {
        throw new Offline();
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.access_token) throw new Error(body.msg ?? "that code did not work");
      return {
        access_token: body.access_token,
        refresh_token: body.refresh_token,
        expires_at: Math.floor(Date.now() / 1000) + (body.expires_in ?? 3600),
      };
    },
  };
}
